import { Link, useLocation } from "react-router-dom";
import MinistryIcon from "./MinistryIcon";
import type { AttentionCard } from "../types/dashboard";
import { roleLabel } from "../lib/role-label";

interface Props {
  cards: AttentionCard[];
  onDismiss: (cardId: string) => void;
  // Clicking a pending action_card expands the embedded Ask panel and
  // focuses that card (v1.1 — no /chat route). Receives the underlying
  // ActionCard id.
  onOpenCard: (actionCardId: string) => void;
}

// Reminder-flavor kinds get a Dismiss "×" Colleen can use to clear
// the card without opening it. Stale sub-requests stay non-
// dismissable — those reflect open gaps that need work. Dismissing
// an action_card is the real thing (server-side, permanent), wired
// up in Dashboard's onDismiss.
const DISMISSABLE_KINDS = new Set<AttentionCard["kind"]>([
  "outreach_pending",
  "quarter_prep",
  "action_card",
]);

export default function NeedsAttention({ cards, onDismiss, onOpenCard }: Props) {
  const location = useLocation();

  return (
    <div className="dashboard__section">
      <h2 id="attention-title" className="dashboard__section-label">
        Sub-requests
        {cards.length > 0 ? (
          <span
            className="panel__title-count"
            aria-label={`${cards.length} open sub-request${cards.length === 1 ? "" : "s"}`}
          >
            {cards.length}
          </span>
        ) : null}
      </h2>
      <section className="panel" aria-labelledby="attention-title">
        {cards.length === 0 ? (
          <p className="panel__empty">All sub-requests are covered.</p>
        ) : (
          <div className="attention__stack">
            {cards.map((card) => {
          const canDismiss = DISMISSABLE_KINDS.has(card.kind);
          const isStructuredSubRequest =
            card.kind === "stale_sub_request" &&
            card.massLongLabel &&
            card.requesterName &&
            card.ministry;
          // Clicking the card opens the gap modal — or whichever
          // detail surface the card's actions point at. Prefer the
          // "See more" secondary; fall back to the primary so a
          // primary-only card (quarter_prep → /templates) is still
          // reachable.
          const target = card.secondaryAction?.href ?? card.primaryAction?.href;

          // Sub-request cards have exactly one hover action: "See
          // more", which opens the modal (the card's `primaryAction` —
          // "Draft email to sacristan" — already lives there as a
          // prominent button, so it's dropped from the hover reveal).
          const subActions =
            isStructuredSubRequest && card.secondaryAction
              ? [card.secondaryAction]
              : [];

          // Sub-request hierarchy: the two most-important facts share
          // line 1 — Mass date/time (left, display serif, the anchor)
          // and who requested the sub (right, muted sans, truncates if
          // long). Line 2 is the role category: "[icon] Usher", the
          // icon inline-left of a confident, near-ink role label.
          const content = isStructuredSubRequest ? (
            <div className="attention__sub">
              <div className="attention__sub-top">
                <div className="attention__sub-mass">{card.massLongLabel}</div>
                <div className="attention__sub-name">{card.requesterName}</div>
              </div>
              <div className="attention__sub-meta">
                <span className="attention__sub-role">
                  <MinistryIcon ministry={card.ministry!} />
                  <span>
                    {card.position
                      ? `${roleLabel(card.ministry!)} — ${card.position}`
                      : roleLabel(card.ministry!)}
                  </span>
                </span>
              </div>
            </div>
          ) : (
            <div className="attention__top">
              <div className="attention__top-left">
                <div className="attention__title">{card.title}</div>
                <div className="attention__body attention__body--clamped">
                  {card.body}
                </div>
              </div>
            </div>
          );

          const openLabel = isStructuredSubRequest
            ? `Open sub-request for ${card.massLongLabel}`
            : `Open ${card.title}`;

          return (
            <article
              key={card.id}
              className={`attention attention--${card.kind}${
                subActions.length > 0
                  ? ` attention--gutter-${subActions.length}`
                  : ""
              }`}
            >
              {card.kind === "action_card" ? (
                // Expands the embedded Ask panel focused on this card —
                // stays on the dashboard, no navigation.
                <button
                  type="button"
                  className="attention__open attention__open--button"
                  aria-label={`Open ${card.title}`}
                  onClick={() => card.actionCardId && onOpenCard(card.actionCardId)}
                >
                  {content}
                </button>
              ) : target ? (
                <Link
                  to={target}
                  state={{ backgroundLocation: location }}
                  className={`attention__open${
                    isStructuredSubRequest ? " attention__open--sub" : ""
                  }`}
                  aria-label={openLabel}
                >
                  {content}
                </Link>
              ) : (
                <div className="attention__open attention__open--static">
                  {content}
                </div>
              )}
              {subActions.length > 0 ? (
                // Quick actions surface on hover/focus inside a
                // permanently reserved right gutter (see the
                // attention--gutter-* rules), so revealing them never
                // overlaps the text column. The card link itself
                // already reaches the same gap modal for touch users.
                <div className="attention__sub-actions">
                  {subActions.map((a) => (
                    <Link
                      key={a.label}
                      to={a.href}
                      state={{ backgroundLocation: location }}
                      className="attention__sub-btn"
                    >
                      {a.label}
                    </Link>
                  ))}
                </div>
              ) : null}
              {canDismiss ? (
                <button
                  type="button"
                  className="attention__dismiss"
                  aria-label={`Dismiss ${card.title}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(card.id);
                  }}
                >
                  ×
                </button>
              ) : null}
            </article>
          );
        })}
          </div>
        )}
      </section>
    </div>
  );
}
